import { clamp01 } from '@/libs/tpm-analytics/core/aggregation.utils';

/**
 * Struktura výstupu OEE komponent – všechny hodnoty jsou v intervalu 0..1 nebo null (pokud nelze spočítat).
 */
export type OeeComponents = {
    availability: number | null;
    performance: number | null;
    quality: number | null;
    oee: number | null;
};

/**
 * Spočítá OEE komponenty z elementárních vstupů.
 *
 * Poznámky k chování:
 * - Všechny výstupy jsou ořezané na 0..1 (clamp), aby nedošlo k přetečení při šumu v datech.
 * - Pokud chybí potřebný dělitel (např. plán nebo produkce), vrací null místo 0, ať je jasné, že údaj není k dispozici.
 */
export function computeOeeComponents(
    okCount: number,
    nokCount: number,
    planCount: number,
    plannedMinutes: number,
    downtimeMinutes: number,
    idealRuntimeMinutes?: number | null
): OeeComponents {
    // Součty s ochranou proti NaN a záporným hodnotám
    const total = Math.max(0, Number(okCount || 0) + Number(nokCount || 0));
    // Kvalita: podíl OK na celku
    const qualityRaw = total > 0 ? Number(okCount || 0) / total : null;
    // Dostupnost: 1 - prostoje / plánovaná doba
    const availabilityRaw =
        Number(plannedMinutes || 0) > 0 ? 1 - Number(downtimeMinutes || 0) / Number(plannedMinutes || 0) : null;
    const availabilityMinutes =
        typeof availabilityRaw === 'number' && typeof downtimeMinutes === 'number'
            ? Number(plannedMinutes || 0) - Number(downtimeMinutes || 0)
            : null;
    // Výkon: preferuj nový vzorec (součet(count_i * ct_i) / dostupný čas), fallback na ok/planCount
    const performanceRaw =
        idealRuntimeMinutes != null && typeof availabilityMinutes === 'number' && availabilityMinutes > 0
            ? Number(idealRuntimeMinutes || 0) / availabilityMinutes
            : null;
    const availability = clamp01(availabilityRaw);
    const performance = clamp01(performanceRaw);
    const quality = clamp01(qualityRaw);
    // OEE je součin tří komponent; pokud některá chybí, vracíme null
    const oeeRaw = [availability, performance, quality].every((x) => typeof x === 'number')
        ? (availability as number) * (performance as number) * (quality as number)
        : null;
    // Poznámka: clampujem i výsledné OEE – chrání proti nečekaným záporným/ >1 hodnotám způsobeným šumem v datech
    return {
        availability,
        performance,
        quality,
        oee: clamp01(oeeRaw),
    };
}
